import type { FC } from "react";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/Chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card } from "@/components/podkit/Card";
import { Text } from "@/components/podkit/typography/Text";
import { OCU } from "@/routes/settings/organization/billing/OCU";

export type ConsumptionChartDataPoint = {
    date: string;
    [key: string]: string | number;
};

type Props = {
    title: string;
    dataKey: string;
    data: ConsumptionChartDataPoint[];
    color: string;
    totalOCU?: number;
    daysLabel?: string;
    /** Optional action element rendered at the top-right of the card header. */
    headerAction?: React.ReactNode;
};

export const ConsumptionChart: FC<Props> = ({
    title,
    dataKey,
    data,
    color,
    totalOCU,
    daysLabel = "7 days",
    headerAction,
}) => {
    const gradientId = `fill-${dataKey}`;

    const chartConfig = {
        [dataKey]: {
            label: title,
            color,
        },
    } satisfies ChartConfig;

    return (
        <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
                <Text className="text-lg font-semibold text-content-primary">
                    {title}
                    {totalOCU !== undefined && (
                        <span className="ml-2 text-sm font-normal text-content-secondary">
                            ({Math.round(totalOCU).toLocaleString()} <OCU /> in {daysLabel})
                        </span>
                    )}
                </Text>
                {headerAction}
            </div>

            <ChartContainer config={chartConfig} className="h-[250px] w-full">
                <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                            <stop offset="95%" stopColor={color} stopOpacity={0.06} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        minTickGap={32}
                    />
                    <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        tickFormatter={(value: number) =>
                            value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toString()
                        }
                    />
                    <ChartTooltip
                        content={
                            <ChartTooltipContent
                                valueFormatter={(value) =>
                                    `${Math.round(Number(value)).toLocaleString()} OCU`
                                }
                            />
                        }
                    />
                    <Area
                        type="linear"
                        dataKey={dataKey}
                        stroke={color}
                        fill={`url(#${gradientId})`}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ChartContainer>
        </Card>
    );
};
